import {
  GetQuestionReviewTaskByIdDocument,
  QuestionGroupType,
  QuestionStatus,
  useGetStepTransitionsByTaskIdLazyQuery,
  useUpdateQuestionReviewTaskStatusMutation,
} from '@exam-management/graphql';
import { isTaskOverdueError } from '@exam-management/task-management';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Flex,
  Form,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useForm, FormProvider } from 'react-hook-form';

import { useQuestionReviewTaskDetails } from '../../providers';
import { sortTaskByCode } from '../../utils/sort-task-by-code';
import { UpdateQuestionStatusList } from '../update-question-status-list';

import {
  type FinishQuestionReviewMainTaskFormInput,
  FinishQuestionReviewMainTaskFormSchema,
} from './constants';

interface QContentImportModalProps {
  onClose: () => void;
  onOverdue: () => void;
}

export default function FinishQuestionReviewMainTaskForm(
  props: Readonly<QContentImportModalProps>,
) {
  const { onClose, onOverdue } = props;

  const { taskDetails, questions } = useQuestionReviewTaskDetails();
  const { showSnackbar } = useSnackbar();

  const [updateQuestionReviewTaskStatus] = useUpdateQuestionReviewTaskStatusMutation({
    onCompleted: () => {
      showSnackbar({ message: 'Task completed successfully', variant: 'success' });
      onClose();
      reset();
    },
    onError: (error) => {
      if (isTaskOverdueError(error)) {
        onClose();
        onOverdue();
        reset();
        return;
      }
      showSnackbar({
        message: 'Unable to finish task',
        variant: 'error',
      });
      onClose();
      reset();
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQuestionReviewTaskByIdDocument,
        variables: {
          input: {
            taskId: taskDetails.id,
          },
        },
      },
    ],
  });

  const [getStepTransitionsByTaskId] = useGetStepTransitionsByTaskIdLazyQuery();

  const formContext = useForm<FinishQuestionReviewMainTaskFormInput>({
    defaultValues: {
      questions: {
        ...questions.reduce(
          (acc, question) => {
            acc[question.id] = QuestionStatus.Draft;
            return acc;
          },
          {} as Record<
            string,
            QuestionStatus.Draft | QuestionStatus.Disabled | QuestionStatus.PreDraft
          >,
        ),
        ...questions
          .filter((q) => q.questionGroup?.type === QuestionGroupType.ItemSet && q.questionGroup?.id)
          .reduce(
            (acc, question) => {
              const groupId = question.questionGroup?.id;
              if (groupId && !acc[groupId]) {
                acc[groupId] = QuestionStatus.Draft;
              }
              return acc;
            },
            {} as Record<
              string,
              QuestionStatus.Draft | QuestionStatus.Disabled | QuestionStatus.PreDraft
            >,
          ),
      },
    },
    resolver: zodResolver(FinishQuestionReviewMainTaskFormSchema),
  });

  const { handleSubmit, reset } = formContext;

  const onSubmit = async (data: FinishQuestionReviewMainTaskFormInput) => {
    const auditors = [...taskDetails.subtasks].sort(sortTaskByCode).reduce(
      (acc, subtask, index) => {
        acc[`audit${index + 1}`] = `${subtask.assignee.firstname} ${subtask.assignee.lastname}`;
        return acc;
      },
      {} as Record<string, string>,
    );

    // Build a map of questionGroupId → questionIds for ItemSet groups
    const groupToQuestionIds = new Map<string, string[]>();
    const itemSetQuestionIds = new Set<string>();
    for (const question of questions) {
      const groupId = question.questionGroup?.id;
      if (question.questionGroup?.type === QuestionGroupType.ItemSet && groupId) {
        itemSetQuestionIds.add(question.id);
        const existing = groupToQuestionIds.get(groupId);
        if (existing) {
          existing.push(question.id);
        } else {
          groupToQuestionIds.set(groupId, [question.id]);
        }
      }
    }

    // Expand grouped entries into individual question entries
    const expandedQuestions: { id: string; status: QuestionStatus }[] = [];
    for (const [id, status] of Object.entries(data.questions)) {
      const questionIds = groupToQuestionIds.get(id);
      if (questionIds) {
        // This is a group entry — expand to individual questions
        for (const questionId of questionIds) {
          expandedQuestions.push({ id: questionId, status });
        }
      } else if (!itemSetQuestionIds.has(id)) {
        expandedQuestions.push({ id, status });
      }
    }

    const metadata = {
      questions: expandedQuestions.map(({ id, status }) => ({
        id,
        status,
        auditors,
      })),
    };

    const result = await getStepTransitionsByTaskId({
      variables: {
        getStepTransitionsByTaskIdInput: {
          taskId: taskDetails.id,
        },
      },
    });

    const stepTransitions = result.data?.wflGetStepTransitionsByTaskId;

    if (stepTransitions) {
      await updateQuestionReviewTaskStatus({
        variables: {
          input: {
            taskId: taskDetails.id,
            workflowStepId: stepTransitions.steps[0].id,
            metadata,
          },
        },
      });
    }
  };

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}
      >
        <Divider />
        <DialogContent
          sx={{
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <Flex
            sx={{
              gap: 4,
              flexDirection: 'column',
            }}
          >
            <Alert severity='info' sx={{ width: '100%', fontWeight: 400 }}>
              เลือกสถานะของคำถามแต่ละข้อ เพื่อกำหนดว่าคำถามใดพร้อมเข้าคลัง (Draft)
              ต้องรีวิวเพิ่มเติม (Pre-Draft) หรือไม่ต้องการใช้งานอีกต่อไป (Disabled)
            </Alert>
            <UpdateQuestionStatusList />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button variant='outlined' onClick={onClose} color='secondary' size='large'>
            Cancel
          </Button>
          <Button
            type='submit'
            color='primary'
            size='large'
            loading={formContext.formState.isSubmitting}
          >
            Confirm
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
